import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthedRequest } from "shared/@auth/Auth";
import FormattedName from "shared/FormattedName";
import { useQueryParams } from "shared/@hooks/useQueryParams";
import { RequestCache } from "shared/@hooks/useRequestCache";
import Link from "shared/Link";
import LoadingContainer from "shared/LoadingContainer";
import PagingControls from "shared/PagingControls";
import PinLabel from "shared/PinLabel";
import Table from "shared/Table";
import TableSortHeader, { TableSortValue } from "shared/TableSortHeader";
import TableFilterSortHeader from "../TableFilterSortHeader";
import styles from "./index.module.css";
import {
  defaultStatusFilter,
  membershipOf,
  normalizeValue,
  parseCsvFilter,
  pinOf,
  sortToHeaderValue,
  statusOf,
  toCsvFilter,
  toLabel,
  toOptions,
} from "../tableUtils";

export default function Report({ url, queryKey }) {
  const query = useQueryParams();
  const cache = RequestCache.useContainer();

  const q = query.get();

  const keyed = useCallback((value) => `${queryKey}-${value}`, [queryKey]);

  const sort = q[keyed("sort")] ?? "name-asc";

  const statusFilters = parseCsvFilter(q[keyed("status")], defaultStatusFilter);
  const membershipFilters = parseCsvFilter(q[keyed("membership")]);
  const pinFilters = parseCsvFilter(q[keyed("pin")]);

  const hasExplicitPinFilter = Object.prototype.hasOwnProperty.call(
    q,
    keyed("pin")
  );
  const hasExplicitMembershipFilter = Object.prototype.hasOwnProperty.call(
    q,
    keyed("membership")
  );
  // Status defaults to current and is treated as an explicit filter by design.
  const hasExplicitStatusFilter = true;

  // Define paging options.
  const page = Math.max(0, parseInt(q[keyed("page")] ?? "0", 10));
  const limit = parseInt(q[keyed("limit")] ?? "25", 10);

  // Build filter query string to send to the backend.
  const filterParams = [
    statusFilters.length > 0
      ? `status=${encodeURIComponent(statusFilters.join(","))}`
      : null,
    membershipFilters.length > 0
      ? `membership=${encodeURIComponent(membershipFilters.join(","))}`
      : null,
    pinFilters.length > 0
      ? `pin=${encodeURIComponent(pinFilters.join(","))}`
      : null,
  ]
    .filter(Boolean)
    .join("&");

  const requestUrl =
    url +
    `?page=${page + 1}&limit=${limit}&sort=${sort}` +
    (filterParams ? `&${filterParams}` : "");

  const { data, loading } = useAuthedRequest(requestUrl, null, cache);

  const [results, setResults] = useState(null);
  const [paging, setPaging] = useState(null);
  const [filters, setFilters] = useState(null);
  const [dropdownHeights, setDropdownHeights] = useState({});

  useEffect(() => {
    if (data != null) {
      setResults(data.data);
      setPaging(data.paging);
      setFilters(data.filters);
    }
  }, [data]);

  // Reset filter/sort params when navigating to a different IBO (url changes).
  useEffect(() => {
    return () => {
      query.set({
        [keyed("sort")]: null,
        [keyed("status")]: null,
        [keyed("membership")]: null,
        [keyed("pin")]: null,
      });
    };
  }, [url]); // eslint-disable-line react-hooks/exhaustive-deps

  const statusOptions = useMemo(() => {
    const values = (
      filters?.status ?? (results ?? []).map((item) => statusOf(item))
    ).map((v) => normalizeValue(v));
    return toOptions(values, ["current"]);
  }, [filters, results]);

  const membershipOptions = useMemo(() => {
    const values = (
      filters?.membership ?? (results ?? []).map((item) => membershipOf(item))
    ).map((v) => normalizeValue(v));
    return toOptions(values);
  }, [filters, results]);

  const pinOptions = useMemo(() => {
    const values = filters?.pin ?? (results ?? []).map((item) => pinOf(item));
    return toOptions(values);
  }, [filters, results]);

  // Check if filters are fully selected (all available options chosen)
  const allStatusesSelected = useMemo(() => {
    if (statusOptions.length === 0) return true;
    return (
      statusFilters.length === statusOptions.length &&
      statusOptions.every((opt) => statusFilters.includes(opt.value))
    );
  }, [statusFilters, statusOptions]);

  const allMembershipsSelected = useMemo(() => {
    if (membershipOptions.length === 0) return true;
    return (
      membershipFilters.length === membershipOptions.length &&
      membershipOptions.every((opt) => membershipFilters.includes(opt.value))
    );
  }, [membershipFilters, membershipOptions]);

  const allPinsSelected = useMemo(() => {
    if (pinOptions.length === 0) return true;
    return (
      pinFilters.length === pinOptions.length &&
      pinOptions.every((opt) => pinFilters.includes(opt.value))
    );
  }, [pinFilters, pinOptions]);

  const implicitPinCheckedValues = useMemo(() => {
    return [...new Set((results ?? []).map((item) => pinOf(item)))];
  }, [results]);

  const implicitStatusCheckedValues = useMemo(() => {
    return [...new Set((results ?? []).map((item) => statusOf(item)))];
  }, [results]);

  const implicitMembershipCheckedValues = useMemo(() => {
    return [...new Set((results ?? []).map((item) => membershipOf(item)))];
  }, [results]);

  const onDropdownMenuStateChange = useCallback((key, isOpen, height) => {
    setDropdownHeights((prev) => {
      if (!isOpen || !height) {
        if (prev[key] == null) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      }
      if (prev[key] === height) return prev;
      return { ...prev, [key]: height };
    });
  }, []);

  const dropdownBottomSpace = useMemo(() => {
    const heights = Object.values(dropdownHeights);
    const maxHeight = heights.length > 0 ? Math.max(...heights) : 0;
    return maxHeight > 0 ? maxHeight + 12 : 0;
  }, [dropdownHeights]);

  // TODO: Remove -1 when route is updated to use 0 indexed paging.
  const lastPage = paging && paging.last - 1;

  return (
    <LoadingContainer isLoading={loading || results == null}>
      <div className={styles.topControls}>
        <PagingControls
          page={page}
          lastPage={lastPage}
          limit={limit}
          onPageChange={(value) => query.set({ [keyed("page")]: value })}
          onLimitChange={(value) => query.set({ [keyed("limit")]: value })}
          disableNext={paging?.last <= paging?.page}
          disabled={loading}
        />
      </div>

      <div
        style={
          dropdownBottomSpace > 0
            ? { paddingBottom: `${dropdownBottomSpace}px` }
            : undefined
        }
      >
        <Table allowContentOverflow={true}>
          <thead>
            <tr>
              <th>
                <TableSortHeader
                  value={
                    sort === "name-asc"
                      ? TableSortValue.ascending
                      : sort === "name-desc"
                      ? TableSortValue.descending
                      : TableSortValue.none
                  }
                  onChange={(value) => {
                    switch (value) {
                      case TableSortValue.ascending:
                        query.set({ [keyed("sort")]: "name-asc" });
                        break;
                      case TableSortValue.descending:
                        query.set({ [keyed("sort")]: "name-desc" });
                        break;
                      case TableSortValue.none:
                        query.set({ [keyed("sort")]: null });
                        break;
                    }
                  }}
                >
                  Name
                </TableSortHeader>
              </th>

              <th>
                <TableSortHeader
                  value={
                    sort === "ibo-asc"
                      ? TableSortValue.ascending
                      : sort === "ibo-desc"
                      ? TableSortValue.descending
                      : TableSortValue.none
                  }
                  onChange={(value) => {
                    switch (value) {
                      case TableSortValue.ascending:
                        query.set({ [keyed("sort")]: "ibo-asc" });
                        break;
                      case TableSortValue.descending:
                        query.set({ [keyed("sort")]: "ibo-desc" });
                        break;
                      case TableSortValue.none:
                        query.set({ [keyed("sort")]: null });
                        break;
                    }
                  }}
                >
                  IBO
                </TableSortHeader>
              </th>

              <th>
                <TableFilterSortHeader
                  title="Pin Level"
                  options={pinOptions}
                  selectedValues={pinFilters}
                  implicitCheckedValues={implicitPinCheckedValues}
                  isExplicitSelection={hasExplicitPinFilter}
                  onMenuStateChange={(isOpen, height) =>
                    onDropdownMenuStateChange("pin", isOpen, height)
                  }
                  onFilterChange={(next) => {
                    query.set({
                      [keyed("pin")]: allPinsSelected
                        ? null
                        : toCsvFilter(next),
                      [keyed("page")]: 0,
                    });
                  }}
                  sortValue={sortToHeaderValue(sort, "pin")}
                  onSortChange={(value) => {
                    query.set({
                      [keyed("sort")]:
                        value === TableSortValue.ascending
                          ? "pin-asc"
                          : "pin-desc",
                    });
                  }}
                />
              </th>

              <th>
                <TableFilterSortHeader
                  title="Status"
                  options={statusOptions}
                  selectedValues={statusFilters}
                  implicitCheckedValues={implicitStatusCheckedValues}
                  isExplicitSelection={hasExplicitStatusFilter}
                  onMenuStateChange={(isOpen, height) =>
                    onDropdownMenuStateChange("status", isOpen, height)
                  }
                  onFilterChange={(next) => {
                    query.set({
                      [keyed("status")]: allStatusesSelected
                        ? null
                        : toCsvFilter(next),
                      [keyed("page")]: 0,
                    });
                  }}
                  sortValue={sortToHeaderValue(sort, "status")}
                  onSortChange={(value) => {
                    query.set({
                      [keyed("sort")]:
                        value === TableSortValue.ascending
                          ? "status-asc"
                          : "status-desc",
                    });
                  }}
                />
              </th>

              <th>
                <TableFilterSortHeader
                  title="Membership"
                  options={membershipOptions}
                  selectedValues={membershipFilters}
                  implicitCheckedValues={implicitMembershipCheckedValues}
                  isExplicitSelection={hasExplicitMembershipFilter}
                  onMenuStateChange={(isOpen, height) =>
                    onDropdownMenuStateChange("membership", isOpen, height)
                  }
                  onFilterChange={(next) => {
                    query.set({
                      [keyed("membership")]: allMembershipsSelected
                        ? null
                        : toCsvFilter(next),
                      [keyed("page")]: 0,
                    });
                  }}
                  sortValue={sortToHeaderValue(sort, "membership")}
                  onSortChange={(value) => {
                    query.set({
                      [keyed("sort")]:
                        value === TableSortValue.ascending
                          ? "membership-asc"
                          : "membership-desc",
                    });
                  }}
                />
              </th>

              <th>Email</th>
            </tr>
          </thead>

          <tbody>
            {results != null &&
              (results.length > 0 ? (
                results.map((ibo) => (
                  <tr key={ibo.iboNumber}>
                    <td>
                      <FormattedName value={ibo.fullName} />
                    </td>
                    <td>
                      <Link to={`/membership/lookup/ibo/${ibo.iboNumber}`}>
                        {ibo.iboNumber}
                      </Link>
                    </td>
                    <td>
                      <PinLabel pinLevel={ibo.pinLevel} />
                    </td>

                    <td>{toLabel(normalizeValue(ibo.status))}</td>

                    <td>
                      {toLabel(normalizeValue(ibo.membership))}
                      {ibo.hasDigitalDelivery && ibo.hasDreamStream
                        ? " +"
                        : ibo.hasDigitalDelivery
                        ? " + DD"
                        : ibo.hasDreamStream
                        ? " + Topics"
                        : ""}
                    </td>

                    <td>
                      <span className={styles.email}>{ibo.email}</span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6}>
                    <div>No results found.</div>
                  </td>
                </tr>
              ))}
          </tbody>
        </Table>
      </div>

      <div className={styles.bottomControls}>
        <PagingControls
          page={page}
          lastPage={lastPage}
          limit={limit}
          onPageChange={(value) => query.set({ [keyed("page")]: value })}
          onLimitChange={(value) => query.set({ [keyed("limit")]: value })}
          disableNext={paging?.last <= paging?.page}
          disabled={loading}
        />
      </div>
    </LoadingContainer>
  );
}
